import { useEffect, useState } from "react";
import { FileText, Eye, Printer, Loader2, Mail, Send, X } from "lucide-react";
import { http, toastApiError } from "../../lib/api";
import { toast } from "../../lib/notify";
import { inp, lbl, btnDark, btnLight } from "./ledgerUtils";

const txt = (el) => (el?.textContent || "").replace(/\s+/g, " ").trim();
const isRight = (el) => /text-right/.test(el?.className || "");

// Konteyner içindeki (iç içe olmayan) tablolar → PDF bölümleri; data-pdf-title taşıyan en yakın üst öğe bölüm başlığı olur
export function tablesToSections(root) {
  if (!root) return [];
  return [...root.querySelectorAll("table")].filter((t) => !t.parentElement.closest("table")).map((t) => {
    const ths = [...t.querySelectorAll(":scope > thead th")];
    const cell = (td) => [txt(td), ...Array(Math.max(1, td.colSpan || 1) - 1).fill("")];   // colSpan'lı TOPLAM hücresi başlık sayısı kadar açılır → rakamlar doğru sütunda
    const rowsOf = (sel) => [...t.querySelectorAll(sel)].filter((tr) => !tr.querySelector("table") && !tr.hasAttribute("data-pdf-skip")).map((tr) => [...tr.children].flatMap(cell));
    const rows = rowsOf(":scope > tbody > tr"), footer = rowsOf(":scope > tfoot > tr");
    const first = t.querySelector(":scope > tbody > tr");
    const aligns = (ths.length ? ths : [...(first?.children || [])]).map((c) => (isRight(c) ? "R" : "L"));
    return { title: t.closest("[data-pdf-title]")?.getAttribute("data-pdf-title") || "", headers: ths.map(txt), rows, footer, aligns };
  }).filter((s) => s.rows.length || s.footer.length);
}

// Raporu PDF ekiyle e-posta gönderme (mali müşavir adresi Firma Bilgileri'nden önerilir); Esc kapatır
function EmailDialog({ payload, company, endpoint, onClose }) {
  const [f, setF] = useState({ to: "", cc: "", message: "" });
  const [advisor, setAdvisor] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get("/ledger/companies").then((r) => { const c = r.data[company] || {}; setAdvisor(c); setF((s) => ({ ...s, to: s.to || c.advisor_email || "" })); }).catch(() => setAdvisor({}));
  }, [company]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await http.post(`${endpoint}/email`, { ...payload, to: f.to.trim(), cc: f.cc.split(/[;,\s]+/).filter(Boolean), message: f.message });
      if (data.status === "sent") toast.success(`${data.filename} ${data.to} adresine gönderildi`);
      else if (data.status === "queued") toast.success(`E-posta kuyruğa alındı (${data.to}) — e-posta servisi yapılandırılınca gönderilir`);
      else toast.error(`Gönderilemedi: ${data.error || "bilinmeyen hata"}`);
      if (data.status !== "failed") onClose();
    } catch (err) { toastApiError(err); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="pdf-email-dialog">
      <form onSubmit={send} className="bg-white rounded-lg shadow-2xl w-full max-w-xl p-5 space-y-4 mt-10" role="dialog" aria-modal="true">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-slate-700" /><span className="font-semibold text-sm">E-posta ile Gönder — {payload.title}</span>
          <span className="ml-auto text-[11px] text-slate-500">{company}{payload.subtitle ? ` · ${payload.subtitle}` : ""}</span>
        </div>
        <label className={lbl}>Alıcı{advisor?.advisor_name && <span className="ml-1 normal-case tracking-normal text-slate-400">(mali müşavir: {advisor.advisor_name})</span>}
          <input type="email" required value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} placeholder={advisor && !advisor.advisor_email ? "Firma Bilgileri'nde mali müşavir e-postası tanımlayın → otomatik dolar" : "ornek@musavir.com"} className={`${inp} block w-full`} autoFocus data-testid="pdf-email-to" /></label>
        <label className={lbl}>CC <span className="normal-case tracking-normal text-slate-400">(virgülle ayırın, isteğe bağlı)</span><input value={f.cc} onChange={(e) => setF({ ...f, cc: e.target.value })} className={`${inp} block w-full`} data-testid="pdf-email-cc" /></label>
        <label className={lbl}>Not <span className="normal-case tracking-normal text-slate-400">(e-posta gövdesine eklenir)</span><textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={`${inp} block w-full`} data-testid="pdf-email-message" /></label>
        <div className="text-[11px] text-slate-500">Ek: <span className="font-mono">{payload.title}.pdf</span> — ekrandaki tablo, antetli (logo + firma kimlik + sayfa numarası).</div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnLight} data-testid="pdf-email-cancel"><X className="w-3.5 h-3.5" /> İptal</button>
          <button type="submit" disabled={busy} className={btnDark} data-testid="pdf-email-send">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Gönder</button>
        </div>
      </form>
    </div>
  );
}

// PDF al / Görüntüle / Yazdır / E-posta — tüm defter ve rapor ekranlarında ortak; getSections verilmezse containerRef içindeki tablolar kullanılır
export default function PdfTools({ title, subtitle = "", company = "", containerRef, getSections, landscape = true, testid = "pdf", endpoint = "/ledger/pdf" }) {
  const [busy, setBusy] = useState("");
  const [mail, setMail] = useState(null);
  const build = () => {
    const sections = getSections ? getSections() : tablesToSections(containerRef?.current);
    if (!sections.length) { toast.error("PDF için listelenecek veri yok"); return null; }
    return { title, subtitle, company, landscape, sections };
  };
  const run = async (mode) => {
    const payload = build();
    if (!payload) return;
    if (mode === "email") { setMail(payload); return; }
    setBusy(mode);
    try {
      const r = await http.post(endpoint, payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (mode === "download") {
        const a = document.createElement("a"); a.href = url; a.download = `${title.replace(/[^\p{L}\p{N}_-]+/gu, "_")}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      } else if (mode === "view") {
        window.open(url, "_blank", "noopener");
      } else {
        const f = document.createElement("iframe"); f.style.cssText = "position:fixed;width:0;height:0;border:0;"; f.src = url; document.body.appendChild(f);
        f.onload = () => { f.contentWindow?.focus(); f.contentWindow?.print(); setTimeout(() => f.remove(), 60000); };
      }
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) { toastApiError(e); } finally { setBusy(""); }
  };
  const B = ({ mode, icon: Icon, label, hint }) => (
    <button type="button" onClick={() => run(mode)} disabled={!!busy} title={hint || label} data-testid={`${testid}-${mode}`}
      className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 first:rounded-l-md last:rounded-r-md">
      {busy === mode ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />} {label}
    </button>
  );
  return (
    <>
      <div className="inline-flex items-stretch rounded-md border border-slate-300 bg-white divide-x divide-slate-200" data-testid={`${testid}-tools`}>
        <B mode="download" icon={FileText} label="PDF Al" /><B mode="view" icon={Eye} label="Görüntüle" /><B mode="print" icon={Printer} label="Yazdır" /><B mode="email" icon={Mail} label="E-posta" hint="Raporu PDF ekiyle e-posta gönder (ör. mali müşavire)" />
      </div>
      {mail && <EmailDialog payload={mail} company={company} endpoint={endpoint} onClose={() => setMail(null)} />}
    </>
  );
}
